/**
 * ============================================================================
 * TransactionsTable - Main Transactions Table Component
 * ============================================================================
 *
 * Features:
 * - Proper type safety (no 'any')
 * - Accessible badge components
 * - Clean separation of concerns
 * - Responsive design
 *
 * ============================================================================
 */

import React, { useMemo, useState } from 'react';
import {
  Download,
  Search,
  User,
  Calendar,
  Repeat,
  CreditCard,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useConfirmation } from '../../contexts/ConfirmationContext';
import {
  formatCurrency,
  formatDate,
  getTransactionTypeInfo,
  getStatusInfo,
  exportTransactionsToCSV,
  filterTransactionsBySearch,
} from '../../utils/transactionFormatters';
import TransactionDetailsModal from './TransactionDetailsModal';
import type { Transaction, TransactionType, TransactionStatus, TransactionsFilters } from '../../types/transactions';

interface TransactionsTableProps {
  transactions: Transaction[];
  filters: TransactionsFilters;
  onFilterChange: (filters: Partial<TransactionsFilters>) => void;
  isSplitVenue?: boolean;
}

// Proper badge components (no raw JSX returned)
function TypeBadge({ type }: { type: TransactionType }) {
  if (type === 'payout') return null; // Don't show badge for payouts

  const { label, colorClass } = getTransactionTypeInfo(type);

  const icons = {
    booking: Calendar,
    recurring: Repeat,
    membership: CreditCard,
    refund: RefreshCw,
    payout: DollarSign,
    debt: DollarSign,
  };

  const Icon = icons[type];

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${colorClass}`}
      role="status"
      aria-label={`Transaction type: ${label}`}
    >
      <Icon className="w-3 h-3 mr-1" aria-hidden="true" />
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  const { label, colorClass } = getStatusInfo(status);

  const icons: Record<string, any> = {
    completed: CheckCircle,
    active: CheckCircle,
    processed: CheckCircle,
    pending: Clock,
    cancelled: XCircle,
    refunded: RefreshCw,
    held: Clock,
    eligible: CheckCircle,
    paid: CheckCircle,
    expired: XCircle,
    cleared: CheckCircle,
    processing: Clock,
    scheduled: CheckCircle,
    rescheduled: RefreshCw,
    unknown: XCircle,
  };

  const Icon = icons[status?.toLowerCase() || 'unknown'] || CheckCircle;

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${colorClass}`}
      role="status"
      aria-label={`Status: ${label}`}
    >
      <Icon className="w-3 h-3 mr-1" aria-hidden="true" />
      {label}
    </span>
  );
}

export function TransactionsTable({
  transactions,
  filters,
  onFilterChange,
  isSplitVenue = false,
}: TransactionsTableProps) {
  const { alert } = useConfirmation();

  // Modal state for transaction details
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle row click to open modal
  const handleRowClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  // Client-side filtering (search only)
  const filteredTransactions = useMemo(() => {
    return filterTransactionsBySearch(transactions, filters.search);
  }, [transactions, filters.search]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredTransactions.slice(start, end);
  }, [filteredTransactions, currentPage]);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Export handler with proper error handling
  const handleExport = async () => {
    try {
      exportTransactionsToCSV(filteredTransactions);
    } catch (error) {
      await alert({
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to export transactions.',
        variant: 'danger',
      });
    }
  };

  return (
    <div>
      {/* Filters Bar */}
      <div className="bg-white rounded-xl border border-gray-200 mb-4">
        <div className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <Search
                className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                aria-hidden="true"
              />
              <input
                type="text"
                placeholder="Search transactions..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 w-full text-xs sm:text-sm"
                aria-label="Search transactions"
              />
            </div>

            {/* Filters row */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Type Filter */}
              <select
                value={filters.type}
                onChange={(e) => onFilterChange({ type: e.target.value as TransactionType | 'all' })}
                className="px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-semibold text-xs sm:text-sm bg-white"
                aria-label="Filter by transaction type"
              >
                <option value="all">All Types</option>
                <option value="booking">One-time</option>
                <option value="recurring">Recurring</option>
                <option value="membership">Memberships</option>
                <option value="refund">Refunds</option>
                {!isSplitVenue && <option value="payout">Payouts</option>}
              </select>

              {/* Status Filter */}
              <select
                value={filters.status}
                onChange={(e) => onFilterChange({ status: e.target.value as TransactionStatus | 'all' })}
                className="px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-semibold text-xs sm:text-sm bg-white"
                aria-label="Filter by status"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>

              {/* Export Button */}
              <button
                onClick={handleExport}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-xs sm:text-sm font-semibold hover:bg-blue-700 transition-colors"
                aria-label="Export transactions to CSV"
              >
                <Download className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">Export</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className={`w-full ${isSplitVenue ? 'min-w-[580px]' : 'min-w-[700px]'}`}>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Type & Reference
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Description
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Credit (+)
                </th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Debit (-)
                </th>
                {!isSplitVenue && (
                  <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Balance After
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedTransactions.length === 0 ? (
                <tr>
                  <td colSpan={isSplitVenue ? 6 : 7} className="px-4 py-12 text-center text-gray-500">
                    No transactions found
                  </td>
                </tr>
              ) : (
                paginatedTransactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    onClick={() => handleRowClick(transaction)}
                    className="border-b border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div>
                        <TypeBadge type={transaction.type} />
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          {transaction.booking_reference || transaction.transfer_reference || '-'}
                        </p>
                        {transaction.customer && (
                          <p className="text-xs text-gray-500 flex items-center mt-0.5">
                            <User className="w-3 h-3 mr-1" aria-hidden="true" />
                            {transaction.customer}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const dateStr = transaction.formatted_date || formatDate(transaction.date);
                        // Split date and time for cleaner display
                        const dateObj = new Date(transaction.date);
                        const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        const formattedTime = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                        return (
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{formattedDate}</p>
                            <p className="text-xs text-gray-600">{formattedTime}</p>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {transaction.description}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {transaction.is_credit ? (
                        <span className="text-sm font-bold text-green-600">+{formatCurrency(transaction.amount)}</span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!transaction.is_credit ? (
                        <span className="text-sm font-bold text-red-600">-{formatCurrency(transaction.amount)}</span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    {!isSplitVenue && (
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-semibold ${transaction.balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(transaction.balance)}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <StatusBadge status={transaction.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredTransactions.length > 0 && (
          <div className="px-3 sm:px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              Showing <span className="font-semibold">{(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredTransactions.length)}</span> of <span className="font-semibold">{filteredTransactions.length}</span> transactions
            </p>
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = i + 1;

                // Smart pagination: show pages around current page
                if (totalPages > 5) {
                  if (currentPage > 3) {
                    pageNum = currentPage - 2 + i;
                  }
                  if (pageNum > totalPages) return null;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    aria-label={`Go to page ${pageNum}`}
                    aria-current={currentPage === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
      />
    </div>
  );
}
